import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { LogoFull, LogoMark } from "./Logo";
import axios from "axios";
import { 
  ArrowRight, Check, Zap, FileText, Eye, Clock,
  CreditCard, Mail, Link2, Copy, Shield,
  Smartphone, Globe, BarChart3, TrendingUp, Star,
  Briefcase, Wrench, Users2, PenTool, Building2, LayoutDashboard,
  Receipt, Repeat, Calculator, Palette
} from "lucide-react";

const Landing = () => {
  const { user } = useAuth();
  const [billingCycle, setBillingCycle] = useState("lifetime");
  const [ltdStatus, setLtdStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ltd/status`).then(({ data }) => setLtdStatus(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* HEADER */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid="logo-link" className="cursor-pointer">
            <LogoFull size={30} />
          </a>
          
          <nav className="hidden md:flex items-center gap-6">
            <a href="#how-it-works" className="text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors">How it works</a>
            <a href="#features" className="text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors">Features</a>
            <a href="#pricing" className="text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors">Pricing</a>
            <Link to="/blog" className="text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors" data-testid="nav-blog">Blog</Link>
            <Link to="/help" className="text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors" data-testid="nav-help">Help</Link>
          </nav>

          <nav className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="btn-primary" data-testid="dashboard-link">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="px-4 py-2 font-semibold text-[#0A0A0A] hover:text-[#1A3DFF] transition-colors text-[15px]" data-testid="login-link">Sign in</Link>
                <Link to="/register" className="btn-primary" data-testid="register-link">Get started free</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* LTD BANNER */}
      {ltdStatus?.is_available && (
        <div className="bg-[#0A0A0A] border-b border-white/10">
          <Link
            to="/lifetime"
            className="max-w-[1200px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-center gap-3 group"
            data-testid="ltd-banner"
          >
            <Star className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
            <span className="text-[13px] sm:text-[14px] text-white/80">
              <span className="font-bold text-[#F59E0B]">Lifetime deal — $99 one-time.</span>
              {" "}Only {ltdStatus.remaining} of {ltdStatus.total_spots} spots left.
            </span>
            <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#F59E0B] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
          </Link>
        </div>
      )}

      {/* HERO */}
      <section className="pt-20 pb-24 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="max-w-[540px]">
              <p className="text-[13px] font-semibold text-[#1A3DFF] tracking-[0.08em] uppercase mb-5">
                Stop wasting time on invoices
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] tracking-[-0.03em] text-[#0A0A0A] mb-6">
                Create invoices in<br />
                <span className="text-[#1A3DFF]">30 seconds.</span><br />
                Not 30 features.
              </h1>
              <p className="text-base lg:text-lg leading-[1.6] text-[#6B7280] mb-8 max-w-[440px]">
                No setup. No learning curve. Just create, send, and get paid — via Stripe, PayPal, UPI &amp; Wise.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-start mb-8">
                <Link to="/register" className="btn-secondary inline-flex items-center gap-2.5" data-testid="hero-cta">
                  Create your first invoice
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {/* Free plan perks */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2.5" data-testid="hero-free-perks">
                {[
                  "10 invoices/month — free forever",
                  "Unlimited clients — no per-seat pricing",
                  "Estimates that convert in one click",
                  "Track every expense and receipt",
                  "Stripe, PayPal, UPI & Wise included",
                  "12 tax regions auto-filled",
                  "See your P&L at a glance",
                  "No credit card needed"
                ].map((perk, i) => (
                  <div key={i} className="flex items-center gap-2 text-[13px] text-[#6B7280]">
                    <Check className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
                    {perk}
                  </div>
                ))}
              </div>
            </div>

            {/* Invoice Preview */}
            <div className="relative">
              <div className="bg-white border border-[#E5E7EB] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] p-8">
                <div className="flex justify-between items-start mb-10">
                  <div>
                    <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center mb-3">
                      <span className="text-white font-bold text-xs">AC</span>
                    </div>
                    <p className="font-semibold text-[#0A0A0A]">Acme Studio</p>
                    <p className="text-[13px] text-[#6B7280]">hello@acme.studio</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-1">Invoice</p>
                    <p className="font-mono font-semibold text-[#0A0A0A]">INV-0042</p>
                  </div>
                </div>
                <div className="mb-8">
                  <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-2">Bill to</p>
                  <p className="font-semibold text-[#0A0A0A]">Sarah Chen</p>
                  <p className="text-[14px] text-[#6B7280]">Nexus Ventures</p>
                </div>
                <div className="border-t border-b border-[#E5E7EB] py-4 mb-6">
                  <div className="flex justify-between text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase mb-3">
                    <span>Description</span>
                    <span>Amount</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[14px] text-[#0A0A0A]">Brand Identity Design</span>
                      <span className="font-mono text-[14px] font-medium text-[#0A0A0A]">$4,500.00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[14px] text-[#0A0A0A]">Website Development</span>
                      <span className="font-mono text-[14px] font-medium text-[#0A0A0A]">$8,000.00</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-[#0A0A0A]">Total Due</span>
                  <span className="font-mono text-[28px] font-bold tracking-[-0.02em] text-[#0A0A0A]">$12,500.00</span>
                </div>
                <div className="mt-6 flex gap-2">
                  <div className="flex-1 bg-[#1A3DFF] text-white text-center py-2.5 text-[13px] font-bold">Pay $12,500.00</div>
                  <div className="px-3 py-2.5 border border-[#E5E7EB] text-[13px] font-medium text-[#6B7280]">PDF</div>
                </div>
                <div className="absolute -top-3 -right-3 bg-[#1A3DFF] text-white text-[11px] font-semibold px-3 py-1.5 tracking-[0.02em]">
                  READY TO SEND
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — Speed Story */}
      <section id="how-it-works" className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-[13px] font-semibold text-[#1A3DFF] tracking-[0.08em] uppercase mb-4">No bloat. No BS.</p>
            <h2 className="font-display text-[36px] sm:text-[40px] tracking-[-0.03em] text-white">
              Three steps. Thirty seconds.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="text-[64px] font-display tracking-[-0.04em] text-[#1A3DFF] leading-none mb-4">1</div>
              <h3 className="font-semibold text-[18px] text-white mb-2">Sign up</h3>
              <p className="text-[15px] text-white/50 leading-relaxed">
                Name, email, password. That's it. No company size dropdown, no "how did you hear about us."
              </p>
            </div>
            <div className="relative">
              <div className="text-[64px] font-display tracking-[-0.04em] text-[#1A3DFF] leading-none mb-4">2</div>
              <h3 className="font-semibold text-[18px] text-white mb-2">Create an invoice</h3>
              <p className="text-[15px] text-white/50 leading-relaxed">
                Pick a client, add line items, set a due date. Everything's inline — no page reloads, no modals.
              </p>
            </div>
            <div className="relative">
              <div className="text-[64px] font-display tracking-[-0.04em] text-[#1A3DFF] leading-none mb-4">3</div>
              <h3 className="font-semibold text-[18px] text-white mb-2">Send & get paid</h3>
              <p className="text-[15px] text-white/50 leading-relaxed">
                Email it, share a link, or let clients pay via Stripe, PayPal, UPI, or Wise. Done. Go do actual work.
              </p>
            </div>
          </div>

          <div className="text-center mt-16">
            <Link to="/register" className="inline-flex items-center gap-2.5 bg-[#1A3DFF] text-white px-8 py-3.5 font-bold text-[15px] hover:bg-[#1433CC] transition-colors" data-testid="speed-cta">
              Try it — it's free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="py-10 border-b border-[#E5E7EB] bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="font-display text-[36px] tracking-[-0.03em] text-[#0A0A0A]">30s</p>
              <p className="text-[13px] text-[#6B7280] mt-1">Average invoice time</p>
            </div>
            <div className="text-center">
              <p className="font-display text-[36px] tracking-[-0.03em] text-[#0A0A0A]">12</p>
              <p className="text-[13px] text-[#6B7280] mt-1">Tax regions built-in</p>
            </div>
            <div className="text-center">
              <p className="font-display text-[36px] tracking-[-0.03em] text-[#1A3DFF]">4</p>
              <p className="text-[13px] text-[#6B7280] mt-1">Payment methods</p>
            </div>
            <div className="text-center">
              <p className="font-display text-[36px] tracking-[-0.03em] text-[#0A0A0A]">$0</p>
              <p className="text-[13px] text-[#6B7280] mt-1">To get started</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 bg-[#0A0A0A]">
        <div className="max-w-[1200px] mx-auto">
          <div className="mb-10 sm:mb-14">
            <p className="text-[13px] font-semibold text-[#1A3DFF] tracking-[0.08em] uppercase mb-4">Simple at the core. Powerful when needed.</p>
            <h2 className="font-display text-[28px] sm:text-[40px] tracking-[-0.03em] text-white">
              Everything you need.<br />Nothing you don't.
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-8 sm:gap-y-10">
            {[
              { title: "Get paid your way", desc: "Stripe, PayPal, Razorpay UPI, and Wise — clients choose how to pay you." },
              { title: "Country tax templates", desc: "Auto-fill VAT, GST, CGST+SGST, Sales Tax for 12 regions. Override per invoice." },
              { title: "Tax compliance built-in", desc: "GSTIN, VAT ID, ABN printed on invoices. EU reverse charge notes auto-added." },
              { title: "Sub-second speed", desc: "Every action is instant. Create, edit, send — all under a second." },
              { title: "3 PDF templates", desc: "Classic, Modern, or Minimal. Match your brand with custom colors." },
              { title: "Estimates that close deals", desc: "Send quotes with a shareable link. Clients accept or decline. Convert to invoice in one click." },
              { title: "Recurring on auto-pilot", desc: "Set any invoice to repeat. Weekly, monthly, quarterly — auto-sends to clients." },
              { title: "Email & get paid", desc: "Send professional invoice emails with a personal message. One click." },
              { title: "Share a link, get paid", desc: "Clients view, download PDF, and pay — no account needed on their end." },
              { title: "Know where you stand", desc: "Paid vs outstanding, by client, by date range, with collection rate." },
              { title: "Track every expense", desc: "Log expenses, attach receipts, track by category. See profit & loss at a glance." },
              { title: "Invoice globally", desc: "USD, EUR, GBP, JPY, AUD, CAD, INR, AED, and 12 more currencies." },
              { title: "Works on mobile", desc: "Install as a PWA on your phone. Invoice from anywhere, offline-ready." },
              { title: "AI-powered blog", desc: "Built-in SEO blog engine. Generate articles, rewrite from URLs, boost your Google ranking." },
              { title: "Auto invoice numbers", desc: "Numbers auto-generate sequentially. Or customise per invoice." },
              { title: "Make it yours", desc: "Your logo, your colors, your templates. Remove VeroBill branding on Pro." },
            ].map((f, i) => (
              <div key={i} className="flex gap-3" data-testid={`feature-${i}`}>
                <Check className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[15px] text-white mb-1">{f.title}</h3>
                  <p className="text-[13px] text-white/40 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section id="compare" className="py-24 px-6 bg-white border-y border-[#E5E7EB]">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-[36px] sm:text-[40px] tracking-[-0.03em] text-[#0A0A0A] mb-4">How VeroBill stacks up</h2>
            <p className="text-base lg:text-lg text-[#6B7280]">Most invoicing tools add complexity. We remove it.</p>
          </div>
          
          {/* Comparison Table */}
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <table className="w-full min-w-[800px] text-[13px]">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A]">
                  <th className="text-left py-3 pr-4 font-semibold text-[12px] text-[#9CA3AF] uppercase tracking-wide w-[180px]">Feature</th>
                  <th className="text-center py-3 px-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <LogoMark size={16} />
                      <span className="font-bold text-[14px] text-[#0A0A0A]">VeroBill</span>
                    </div>
                    <span className="text-[11px] text-[#6B7280] font-normal">$9/mo or $99 LTD</span>
                  </th>
                  <th className="text-center py-3 px-3">
                    <span className="font-bold text-[14px] text-[#6B7280]">Billdu</span>
                    <br /><span className="text-[11px] text-[#9CA3AF] font-normal">$4.99-19.99/mo</span>
                  </th>
                  <th className="text-center py-3 px-3">
                    <span className="font-bold text-[14px] text-[#6B7280]">Invoicely</span>
                    <br /><span className="text-[11px] text-[#9CA3AF] font-normal">$9.99-29.99/mo</span>
                  </th>
                  <th className="text-center py-3 px-3">
                    <span className="font-bold text-[14px] text-[#6B7280]">FreshBooks</span>
                    <br /><span className="text-[11px] text-[#9CA3AF] font-normal">$21-65/mo</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Free plan", vb: "Yes — real usage, not crippled", bd: "7-day trial only", inv: "Yes (limited)", fb: "30-day trial only" },
                  { feature: "Free plan clients", vb: "Unlimited", bd: "N/A (no free plan)", inv: "3 clients", fb: "N/A (no free plan)" },
                  { feature: "Payment methods on free", vb: "All 4 (Stripe, PayPal, UPI, Wise)", bd: "N/A", inv: "PayPal only", fb: "N/A" },
                  { feature: "Estimates / Quotes", vb: "Unlimited on all plans (incl. free)", bd: "Standard+ ($9.99/mo)", inv: "All plans", fb: "All plans (from $23/mo)" },
                  { feature: "Expense tracking", vb: "Unlimited (incl. free)", bd: "All plans", inv: "Paid plans", fb: "All plans" },
                  { feature: "Recurring invoices", vb: "All Pro plans ($9/mo)", bd: "Premium only ($19.99/mo)", inv: "Paid plans", fb: "All plans (from $23/mo)" },
                  { feature: "Payment options (paid)", vb: "4 (incl. UPI & Wise)", bd: "2 (Stripe, PayPal)", inv: "Stripe, PayPal", fb: "Card, ACH, Apple/Google Pay" },
                  { feature: "Country tax templates", vb: "12 regions (auto-fill)", bd: "Manual only", inv: "Manual only", fb: "Manual only" },
                  { feature: "Tax compliance (GSTIN, VAT)", vb: "Built-in on PDFs", bd: "No", inv: "No", fb: "No" },
                  { feature: "PDF templates + brand color", vb: "3 templates + color picker", bd: "Customizable", inv: "Basic customization", fb: "Customizable" },
                  { feature: "P&L analytics", vb: "Built-in (incl. free)", bd: "Limited", inv: "Basic", fb: "Reports (from $23/mo)" },
                  { feature: "Lifetime deal", vb: "$99 one-time — use forever", bd: "No", inv: "No", fb: "No" },
                  { feature: "Starting price (monthly)", vb: "Free / $9 Pro", bd: "$4.99 (Lite, 10 clients)", inv: "Free (limited)", fb: "$23 (Lite, 5 clients)" },
                  { feature: "Invoice creation speed", vb: "~30 seconds", bd: "~45 seconds", inv: "~60 seconds", fb: "~2 minutes" },
                  { feature: "Mobile experience", vb: "PWA (installable)", bd: "Native iOS & Android", inv: "Native apps", fb: "Native apps" },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]">
                    <td className="py-3 pr-4 font-medium text-[#0A0A0A]">{row.feature}</td>
                    <td className="py-3 px-3 text-center font-semibold text-[#0A0A0A]">{row.vb}</td>
                    <td className="py-3 px-3 text-center text-[#6B7280]">{row.bd}</td>
                    <td className="py-3 px-3 text-center text-[#6B7280]">{row.inv}</td>
                    <td className="py-3 px-3 text-center text-[#6B7280]">{row.fb}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] text-[#9CA3AF] mt-4">Pricing and features sourced from competitor public websites as of April 2026. Trademarks belong to their respective owners.</p>

          <div className="mt-8 p-6 bg-[#0A0A0A]">
            <p className="text-[15px] text-white font-semibold mb-2">Built for people who work for themselves</p>
            <p className="text-[14px] text-white/60 leading-relaxed">
              Freelance developers, designers, consultants, contractors, coaches, tutors, solopreneurs, and small agency owners. If you bill clients and hate invoicing software, VeroBill is for you. <span className="text-white font-medium">One tool, zero bloat, get paid fast.</span> The only invoice app with built-in tax compliance for 12 countries and 4 payment methods on every invoice.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT'S FREE — Right after comparison */}
      <section className="py-20 px-6 bg-[#F9FAFB] border-b border-[#E5E7EB]" data-testid="whats-free-section">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-12">
            <p className="text-[13px] font-semibold text-[#10B981] tracking-[0.08em] uppercase mb-4">No credit card required</p>
            <h2 className="font-display text-[32px] sm:text-[40px] tracking-[-0.03em] text-[#0A0A0A] mb-3">
              Start free. Stay free. Seriously.
            </h2>
            <p className="text-base text-[#6B7280] max-w-[500px] mx-auto">
              Not a 14-day trial. Not a "freemium" trap. A genuinely useful free plan you can stay on forever.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: FileText, title: "10 invoices/month", desc: "Create, send, and track 10 professional invoices every month." },
              { icon: Users2, title: "Unlimited clients", desc: "Work with as many clients as you want — no extra cost as you grow." },
              { icon: Receipt, title: "Unlimited estimates", desc: "Send quotes with accept/decline links. Convert to invoices in one click." },
              { icon: Calculator, title: "Unlimited expenses", desc: "Log every expense. Track categories, vendors, receipts. See where your money goes." },
              { icon: CreditCard, title: "4 ways to get paid", desc: "Stripe, PayPal, Razorpay UPI, Wise — all included on free. No restrictions." },
              { icon: Globe, title: "12 tax regions", desc: "Auto-fill VAT, GST, Sales Tax. GSTIN/VAT ID printed right on your PDFs." },
              { icon: BarChart3, title: "P&L at a glance", desc: "Revenue dashboard, expense breakdown, profit margin — know where you stand." },
              { icon: Mail, title: "Email invoices", desc: "Send via VeroBill. Clients reply directly to your email." },
              { icon: Palette, title: "Classic PDF template", desc: "Professional layout with your brand color and logo." },
              { icon: Link2, title: "Send a link, get paid", desc: "Clients view, pay, and download — no account needed on their end." },
              { icon: TrendingUp, title: "Invoice globally", desc: "USD, EUR, GBP, INR, AUD, and 15+ more currencies built in." },
              { icon: Smartphone, title: "Mobile PWA", desc: "Install on your phone. Invoice from bed, coffee shop, anywhere." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white border border-[#E5E7EB] p-4 hover:border-[#10B981] transition-colors" data-testid={`free-feature-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#059669]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[14px] text-[#0A0A0A] mb-0.5">{item.title}</p>
                      <p className="text-[12px] text-[#6B7280] leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/register" className="inline-flex items-center gap-2.5 bg-[#059669] text-white px-8 py-3.5 font-bold text-[15px] hover:bg-[#047857] transition-colors" data-testid="free-plan-section-cta">
              Start free — no credit card
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-10">
            <p className="text-[13px] font-semibold text-[#1A3DFF] tracking-[0.08em] uppercase mb-4">Simple pricing</p>
            <h2 className="font-display text-[36px] sm:text-[40px] tracking-[-0.03em] text-[#0A0A0A]">Free forever. Pro when you're ready.</h2>
          </div>
          
          {/* Monthly/Annual/Lifetime Toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex border border-[#E5E7EB] bg-white" data-testid="pricing-toggle">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-5 py-2.5 text-[13px] font-semibold transition-colors ${billingCycle === "monthly" ? "bg-[#0A0A0A] text-white" : "text-[#6B7280] hover:text-[#0A0A0A]"}`}
                data-testid="pricing-monthly-toggle"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={`px-5 py-2.5 text-[13px] font-semibold transition-colors flex items-center gap-2 ${billingCycle === "annual" ? "bg-[#0A0A0A] text-white" : "text-[#6B7280] hover:text-[#0A0A0A]"}`}
                data-testid="pricing-annual-toggle"
              >
                Annual
                <span className={`text-[11px] font-bold px-1.5 py-0.5 ${billingCycle === "annual" ? "bg-[#1A3DFF] text-white" : "bg-[#ECFDF5] text-[#059669]"}`}>-27%</span>
              </button>
              <button
                onClick={() => setBillingCycle("lifetime")}
                className={`px-5 py-2.5 text-[13px] font-semibold transition-colors flex items-center gap-2 ${billingCycle === "lifetime" ? "bg-[#0A0A0A] text-white" : "text-[#6B7280] hover:text-[#0A0A0A]"}`}
                data-testid="pricing-lifetime-toggle"
              >
                Lifetime
                <span className={`text-[11px] font-bold px-1.5 py-0.5 ${billingCycle === "lifetime" ? "bg-[#F59E0B] text-[#0A0A0A]" : "bg-[#FEF3C7] text-[#D97706]"}`}>BEST</span>
              </button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-[700px] mx-auto">
            {/* Free */}
            <div className="bg-white border border-[#E5E7EB] p-8" data-testid="free-plan-card">
              <p className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.08em] uppercase mb-3">Free</p>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-display text-[48px] tracking-[-0.03em] text-[#0A0A0A]">$0</span>
                <span className="text-[14px] text-[#6B7280]">/forever</span>
              </div>
              <p className="text-[14px] text-[#6B7280] mb-8">Everything you need to get paid. No credit card.</p>
              <Link to="/register" className="block text-center w-full py-3 border border-[#E5E7EB] font-semibold text-[14px] text-[#0A0A0A] hover:bg-[#F9FAFB] transition-colors" data-testid="free-plan-cta">
                Get started free
              </Link>
              <ul className="mt-8 space-y-3">
                {[
                  { text: "10 invoices/month", included: true },
                  { text: "Unlimited clients — no per-seat fees", included: true },
                  { text: "Unlimited estimates & quotes", included: true },
                  { text: "Unlimited expense tracking", included: true },
                  { text: "All 4 payment methods included", included: true },
                  { text: "Shareable payment links", included: true },
                  { text: "12 country tax templates", included: true },
                  { text: "20+ currencies", included: true },
                  { text: "Revenue + P&L analytics", included: true },
                  { text: "Brand color customisation", included: true },
                  { text: "Email invoices (via VeroBill)", included: true },
                  { text: "1 PDF template (Classic)", included: true },
                  { text: "Small 'VeroBill' footer on PDFs", included: true },
                  { text: "Recurring invoices", included: false },
                  { text: "3 PDF templates", included: false },
                  { text: "Email from your own domain", included: false },
                  { text: "Remove footer text", included: false },
                ].map((f, i) => (
                  <li key={i} className={`flex items-center gap-2.5 text-[14px] ${f.included ? "text-[#374151]" : "text-[#D1D5DB]"}`}>
                    {f.included ? (
                      <Check className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                    ) : (
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-[#D1D5DB]">—</span>
                    )}
                    {f.text}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro / LTD */}
            <div className={`p-8 relative ${billingCycle === "lifetime" ? "bg-[#0A0A0A] ring-2 ring-[#F59E0B]" : "bg-[#0A0A0A]"}`} data-testid="pro-plan-card">
              <div className={`absolute -top-3 right-6 text-[11px] font-semibold px-3 py-1 ${billingCycle === "lifetime" ? "bg-[#F59E0B] text-[#0A0A0A]" : "bg-[#1A3DFF] text-white"}`}>
                {billingCycle === "lifetime" ? "BEST VALUE" : "POPULAR"}
              </div>
              <p className="text-[11px] font-semibold text-white/50 tracking-[0.08em] uppercase mb-3">
                {billingCycle === "lifetime" ? "Lifetime" : "Pro"}
              </p>
              <div className="flex items-baseline gap-1 mb-1">
                {billingCycle === "lifetime" ? (
                  <>
                    <span className="font-display text-[48px] tracking-[-0.03em] text-[#F59E0B]">$99</span>
                    <span className="text-[14px] text-white/60">one-time</span>
                  </>
                ) : (
                  <>
                    <span className="font-display text-[48px] tracking-[-0.03em] text-white">
                      ${billingCycle === "monthly" ? "9" : "79"}
                    </span>
                    <span className="text-[14px] text-white/60">/{billingCycle === "monthly" ? "month" : "year"}</span>
                  </>
                )}
              </div>
              {billingCycle === "lifetime" ? (
                <p className="text-[13px] text-[#F59E0B] font-semibold mb-2">Pay once, use forever. All future updates included.</p>
              ) : billingCycle === "monthly" ? (
                <p className="text-[13px] text-white/40 mb-2">$108/yr if paid monthly</p>
              ) : (
                <p className="text-[13px] text-[#10B981] font-semibold mb-2">Save $29/year vs monthly</p>
              )}
              <p className="text-[14px] text-white/60 mb-8">
                {billingCycle === "lifetime" ? "For smart professionals who hate subscriptions." : "For professionals who mean business."}
              </p>
              <Link
                to={billingCycle === "lifetime" ? "/lifetime" : "/register"}
                className={`block text-center w-full py-3 font-bold text-[14px] transition-colors ${
                  billingCycle === "lifetime"
                    ? "bg-[#F59E0B] text-[#0A0A0A] hover:bg-[#D97706]"
                    : "bg-[#1A3DFF] text-white hover:bg-[#1433CC]"
                }`}
                data-testid="pro-plan-cta"
              >
                {billingCycle === "lifetime" ? "Get lifetime access — $99" : "Start free, upgrade anytime"}
              </Link>
              <p className="text-[11px] text-white/30 text-center mt-2">Everything in Free, plus:</p>
              <ul className="mt-6 space-y-3">
                {[
                  { text: "Unlimited invoices", highlight: true },
                  { text: "Recurring invoices (auto-send)", highlight: true },
                  { text: "3 PDF templates (Classic, Modern, Minimal)", highlight: true },
                  { text: "Email from your own domain", highlight: true },
                  { text: "Remove VeroBill footer text", highlight: true },
                  { text: "Unlimited clients & estimates" },
                  { text: "Unlimited expense tracking" },
                  { text: "4 payment methods (Stripe, PayPal, UPI, Wise)" },
                  { text: "12 country tax templates + compliance" },
                  { text: "Brand color + 20+ currencies" },
                  { text: "Revenue + P&L analytics" },
                  { text: billingCycle === "lifetime" ? "All future updates — forever" : "Priority support" },
                  { text: billingCycle === "lifetime" ? "No recurring fees, ever" : "Everything in Free included" },
                ].map((f, i) => (
                  <li key={i} className={`flex items-center gap-2.5 text-[14px] ${f.highlight ? "text-white font-medium" : "text-white/60"}`}>
                    <Check className={`w-4 h-4 flex-shrink-0 ${f.highlight ? (billingCycle === "lifetime" ? "text-[#F59E0B]" : "text-[#1A3DFF]") : "text-white/30"}`} />
                    {f.text}
                  </li>
                ))}
              </ul>

              {billingCycle === "lifetime" && ltdStatus?.is_available && (
                <div className="mt-6 pt-4 border-t border-white/10 text-center">
                  <p className="text-[12px] text-white/40">
                    <span className="text-[#F59E0B] font-bold">{ltdStatus.remaining}</span> of {ltdStatus.total_spots} spots remaining
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST SIGNALS */}
      <section className="py-14 px-6 bg-white border-y border-[#E5E7EB]">
        <div className="max-w-[900px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <Shield className="w-6 h-6 text-[#6B7280] mx-auto mb-2" />
              <p className="text-[13px] font-semibold text-[#0A0A0A]">Bank-grade encryption</p>
              <p className="text-[11px] text-[#9CA3AF] mt-0.5">Your API keys are encrypted with Fernet</p>
            </div>
            <div>
              <Zap className="w-6 h-6 text-[#6B7280] mx-auto mb-2" />
              <p className="text-[13px] font-semibold text-[#0A0A0A]">Built for speed</p>
              <p className="text-[11px] text-[#9CA3AF] mt-0.5">Every action under 1 second</p>
            </div>
            <div>
              <Globe className="w-6 h-6 text-[#6B7280] mx-auto mb-2" />
              <p className="text-[13px] font-semibold text-[#0A0A0A]">Global tax compliance</p>
              <p className="text-[11px] text-[#9CA3AF] mt-0.5">12 countries, auto-fill tax IDs</p>
            </div>
            <div>
              <Star className="w-6 h-6 text-[#6B7280] mx-auto mb-2" />
              <p className="text-[13px] font-semibold text-[#0A0A0A]">Indie-built & bootstrapped</p>
              <p className="text-[11px] text-[#9CA3AF] mt-0.5">Real product, real founder, no VC bloat</p>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES — SEO internal links */}
      <section className="py-16 px-6 bg-[#F9FAFB]">
        <div className="max-w-[1000px] mx-auto text-center">
          <h2 className="font-display text-[28px] sm:text-[32px] tracking-[-0.03em] text-[#0A0A0A] mb-3">Built for your industry</h2>
          <p className="text-base text-[#6B7280] mb-10">Whether you're a solo freelancer or running a small agency, VeroBill fits your workflow.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {[
              { name: "Freelancers", slug: "freelancers", icon: Briefcase },
              { name: "Contractors", slug: "contractors", icon: Wrench },
              { name: "Consultants", slug: "consultants", icon: Users2 },
              { name: "Designers", slug: "designers", icon: PenTool },
              { name: "Small Business", slug: "small-business", icon: Building2 },
              { name: "Agencies", slug: "agencies", icon: LayoutDashboard }
            ].map(ind => {
              const Icon = ind.icon;
              return (
                <Link
                  key={ind.slug}
                  to={`/for/${ind.slug}`}
                  className="border border-[#E5E7EB] bg-white px-4 py-3 text-[13px] font-semibold text-[#374151] hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-all flex items-center justify-center gap-2"
                  data-testid={`industry-link-${ind.slug}`}
                >
                  <Icon className="w-4 h-4" />
                  {ind.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-[700px] mx-auto text-center">
          <h2 className="font-display text-4xl sm:text-[44px] tracking-[-0.03em] text-[#0A0A0A] mb-5">
            Invoice sent.<br />Back to work.
          </h2>
          <p className="text-base lg:text-lg text-[#6B7280] mb-10">
            Stop fighting your invoicing software.<br />VeroBill gets out of the way so you can get paid.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/register" className="btn-primary inline-flex items-center gap-2.5 text-[16px]" data-testid="final-cta">
              Create your first invoice
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/lifetime" className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#D97706] hover:text-[#B45309] transition-colors">
              <Star className="w-4 h-4" />
              Or grab the lifetime deal — $99
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5E7EB] py-10 px-6 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-3">Product</p>
              <div className="space-y-2">
                <Link to="/register" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Sign up free</Link>
                <Link to="/lifetime" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Lifetime Deal</Link>
                <Link to="/changelog" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Changelog</Link>
                <Link to="/help" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Help Centre</Link>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-3">Resources</p>
              <div className="space-y-2">
                <Link to="/blog" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Blog</Link>
                <a href="#how-it-works" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">How it works</a>
                <a href="#features" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Features</a>
                <a href="#pricing" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Pricing</a>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-3">Industries</p>
              <div className="space-y-2">
                <Link to="/for/freelancers" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Freelancers</Link>
                <Link to="/for/contractors" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Contractors</Link>
                <Link to="/for/consultants" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Consultants</Link>
                <Link to="/for/designers" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Designers</Link>
                <Link to="/for/small-business" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Small Business</Link>
                <Link to="/for/agencies" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Agencies</Link>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-3">Compare</p>
              <div className="space-y-2">
                <a href="#compare" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">vs Billdu</a>
                <a href="#compare" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">vs Invoicely</a>
                <a href="#compare" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">vs FreshBooks</a>
                <div className="pt-3 mt-3 border-t border-[#F3F4F6]">
                  <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2">Legal</p>
                  <Link to="/privacy" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Privacy Policy</Link>
                  <Link to="/terms" className="block text-[13px] text-[#6B7280] hover:text-[#0A0A0A] mt-2">Terms of Service</Link>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-[#E5E7EB] pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <LogoFull size={22} />
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-[12px] text-[#9CA3AF] hover:text-[#0A0A0A]">Privacy</Link>
              <Link to="/terms" className="text-[12px] text-[#9CA3AF] hover:text-[#0A0A0A]">Terms</Link>
              <p className="text-[12px] text-[#9CA3AF]">&copy; 2026 VeroBill</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
